import type { FC } from "react";
import { useEffect, useRef, useState } from "react";

import { useStoreLocatorContext } from "@locators/components/shared/hooks";
import type { StoreLocatorFilterItem } from "@locators/components/shared/types";
import { OPEN_NOW_CATEGORY, OPEN_NOW_FILTER_OBJ } from "@locators/components/shared/utils";

import { ReplayIcon } from "@jti/brand-icons/replay";
import { Checkbox } from "@jti/core-components/components/Checkbox";
import { useDrawer } from "@jti/ui/components/Drawer/providers";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { StoreLocatorFilterCategorySection } from "../StoreLocatorFilterCategory";

import stylesCategory from "../StoreLocatorFilterCategory/StoreLocatorFilterCategory.module.scss";
import styles from "./StoreLocatorFiltersModal.module.scss";

type StoreLocatorFiltersAll = {
  changeFilters?: (category: string, filter: StoreLocatorFilterItem) => void;
  checkFiltersStatus?: (category: string, filerKey: string) => boolean;
};

export const StoreLocatorFiltersAll: FC<StoreLocatorFiltersAll> = ({ changeFilters, checkFiltersStatus }) => {
  const bodyRef = useRef<HTMLElement | null>(null);
  const { __ } = useTranslations();
  const {
    selectedFiltersNumber,
    newFilters,
    handleResetFilters,
    isOpenNowFilterEnabled,
    isMultipleFiltersSectionOpenEnabled,
    isProductFlavoursLYOFilterEnabled,
    defaultOpenedFilterCategory,
  } = useStoreLocatorContext();
  const [openCategories, setOpenCategories] = useState<string[]>([]);
  const { closeDrawer } = useDrawer();

  useEffect(() => {
    bodyRef.current = document.body;
  }, []);

  useEffect(() => {
    if (newFilters?.[0]) {
      let categoryKey = newFilters[0]?.key;
      if (defaultOpenedFilterCategory) {
        categoryKey = newFilters.find((filter) => filter.key === defaultOpenedFilterCategory)?.key ?? categoryKey;
      }
      setOpenCategories([categoryKey]);
    }
  }, [newFilters, defaultOpenedFilterCategory]);

  const handleCategoryClick = (categoryKey: string) => {
    setOpenCategories((prevCategories) => {
      if (!isMultipleFiltersSectionOpenEnabled) {
        return [categoryKey];
      }

      if (prevCategories.some((category) => category === categoryKey)) {
        return prevCategories.filter((category) => category !== categoryKey);
      }
      return [...prevCategories, categoryKey];
    });
  };

  const handleCloseFilters = () => {
    closeDrawer();
    handleResetFilters();
  };

  return (
    <>
      <div className={styles.filtersTitleContainer}>
        <h2 className={styles.filtersTitle} data-testid="storeLocatorFiltersModalTitle">
          {__("storelocator.filtersby")}
        </h2>
        {!!selectedFiltersNumber && (
          <button
            className={styles.resetButton}
            onClick={handleCloseFilters}
            data-testid="storeLocatorResetFilters"
            aria-label={__("storelocator.reset_filters")}
          >
            <ReplayIcon className={styles.resetButtonIcon} height="20px" width="20px" aria-hidden="true" />
            {__("storelocator.reset_filters")}
          </button>
        )}
      </div>
      <div className={styles.filtersList}>
        {isOpenNowFilterEnabled && (
          <div className={stylesCategory.filtersCategory}>
            <h3 className={stylesCategory.filtersCategoryTitle}>{__("storelocator.filters.category.open_now")}</h3>
            <Checkbox
              label={__("store_locator.popup.shop_status.open")}
              checked={checkFiltersStatus?.(OPEN_NOW_CATEGORY, OPEN_NOW_CATEGORY) ?? false}
              onCheckedChange={() => {
                changeFilters?.(OPEN_NOW_CATEGORY, OPEN_NOW_FILTER_OBJ);
              }}
            />
          </div>
        )}
        {newFilters?.map((filterCategory) => (
          <StoreLocatorFilterCategorySection
            filterCategory={filterCategory}
            openCategories={openCategories}
            isProductFlavoursLYOFilterEnabled={isProductFlavoursLYOFilterEnabled}
            handleCategoryClick={() => handleCategoryClick(filterCategory.key)}
            changeFilters={changeFilters}
            checkFiltersStatus={checkFiltersStatus}
            key={filterCategory.key}
          />
        ))}
      </div>
    </>
  );
};
